"use client";

import { c as _c } from "react/compiler-runtime";
import { useAuth } from "@clerk/nextjs";
import { ArrowRight, Menu } from "lucide-react";
import type { Route } from "next";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useEffect, useState, type ReactNode } from "react";
import { PUBLIC_CONTAINER } from "@/components/public/public-layout-styles";
import { ThemeModeToggle } from "@/components/theme-mode-toggle";
import { AppImage } from "@/components/ui/app-image";
import { Button, ButtonLink } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { authEntryHref, authEntryLabel, authEntryShortLabel } from "@/lib/auth/sign-up";
import { isPrivateStatusUrl } from "@/lib/private-status-route";
import { cn } from "@/lib/utils";
interface PublicLayoutShellProps {
  children: ReactNode;
  currentYear: number;
}
const HEADER_LINK_CLASSES = "hover:bg-muted hover:text-foreground focus-visible:ring-ring focus-visible:ring-offset-background -mx-2 inline-flex items-center rounded-md px-2 py-2.5 text-sm font-medium transition-colors duration-150 ease-out focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none motion-reduce:transition-none";
const PUBLIC_NAV_ITEMS = [{
  href: "/",
  label: "Início",
  isActive: (pathname: string) => pathname === "/"
}, {
  href: "/noticias",
  label: "Notícias",
  isActive: (pathname: string) => pathname.startsWith("/noticias")
}, {
  href: "/legislacao-agro",
  label: "Legislação Agro",
  isActive: (pathname: string) => pathname.startsWith("/legislacao-agro")
}, {
  href: "/planos",
  label: "Planos",
  isActive: (pathname: string) => pathname.startsWith("/planos") || pathname.startsWith("/pricing")
}, {
  href: "/faq",
  label: "Dúvidas",
  isActive: (pathname: string) => pathname.startsWith("/faq")
}] as const;
export function PublicLayoutShell(t0) {
  const $ = _c(5);
  const {
    children,
    currentYear
  } = t0;
  const pathname = usePathname();
  if (isPrivateStatusUrl(pathname)) {
    let t1;
    if ($[0] !== children) {
      t1 = <div className="min-h-dvh bg-black text-white"><main className="mx-auto flex w-full max-w-lg flex-col px-4 py-8">{children}</main></div>;
      $[0] = children;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    return t1;
  }
  let t1;
  if ($[2] !== children || $[3] !== currentYear) {
    t1 = <PublicMarketingShell currentYear={currentYear}>{children}</PublicMarketingShell>;
    $[2] = children;
    $[3] = currentYear;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return t1;
}
function PublicMarketingShell(t0) {
  const $ = _c(30);
  const {
    children,
    currentYear
  } = t0;
  const {
    isSignedIn
  } = useAuth();
  const [scrolled, setScrolled] = useState(false);
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      const onScroll = () => setScrolled(window.scrollY > 8);
      onScroll();
      window.addEventListener("scroll", onScroll, {
        passive: true
      });
      return () => window.removeEventListener("scroll", onScroll);
    };
    t2 = [];
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  useEffect(t1, t2);
  let t3;
  let t4;
  let t5;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <div className="from-brand-primary/20 absolute -top-75 -left-75 size-150 rounded-full bg-linear-to-br to-transparent blur-3xl" />;
    t4 = <div className="from-brand-accent/15 absolute top-[20%] -right-50 size-125 rounded-full bg-linear-to-bl to-transparent blur-3xl" />;
    t5 = <div className="from-brand-primary/10 absolute -bottom-50 left-[30%] size-100 rounded-full bg-linear-to-t to-transparent blur-3xl" />;
    $[2] = t3;
    $[3] = t4;
    $[4] = t5;
  } else {
    t3 = $[2];
    t4 = $[3];
    t5 = $[4];
  }
  let t6;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <div className="pointer-events-none fixed inset-0 overflow-hidden">{t3}{t4}{t5}<div className="absolute inset-0 opacity-[0.015] dark:opacity-[0.03]" style={{
        backgroundImage: "\n\t\t\t\t\t\t\t\t\t\tlinear-gradient(to right, currentColor 1px, transparent 1px),\n\t\t\t\t\t\t\t\t\t\tlinear-gradient(to bottom, currentColor 1px, transparent 1px)\n\t\t\t\t\t\t\t\t\t",
        backgroundSize: "60px 60px"
      }} /></div>;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  const t7 = scrolled && "shadow-sm";
  let t8;
  if ($[6] !== t7) {
    t8 = cn("border-border/50 bg-background fixed inset-x-0 top-0 z-50 border-b transition-shadow duration-200", t7);
    $[6] = t7;
    $[7] = t8;
  } else {
    t8 = $[7];
  }
  let t9;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = cn(PUBLIC_CONTAINER, "flex h-16 items-center justify-between gap-3");
    $[8] = t9;
  } else {
    t9 = $[8];
  }
  let t10;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <AppImage alt="AgroProj" className="h-8 w-32 dark:hidden" height={32} loading="eager" src="/agroproj-logo-text.svg" style={{
      width: "128px",
      height: "32px"
    }} width={128} />;
    $[9] = t10;
  } else {
    t10 = $[9];
  }
  let t11;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <Link aria-label="Homepage" className="flex shrink-0 items-center gap-2" href="/">{t10}<AppImage alt="AgroProj" className="hidden h-8 w-32 dark:block" height={32} loading="eager" src="/agroproj-logo-text-dark.svg" style={{
        width: "128px",
        height: "32px"
      }} width={128} /></Link>;
    $[10] = t11;
  } else {
    t11 = $[10];
  }
  let t12;
  if ($[11] !== isSignedIn) {
    t12 = <div className={t9}>{t11}<HeaderActions isSignedIn={isSignedIn} /></div>;
    $[11] = isSignedIn;
    $[12] = t12;
  } else {
    t12 = $[12];
  }
  let t13;
  if ($[13] !== t12 || $[14] !== t8) {
    t13 = <header className={t8}>{t12}</header>;
    $[13] = t12;
    $[14] = t8;
    $[15] = t13;
  } else {
    t13 = $[15];
  }
  let t14;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <div aria-hidden="true" className="h-16 shrink-0" />;
    $[16] = t14;
  } else {
    t14 = $[16];
  }
  let t15;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = cn(PUBLIC_CONTAINER, "py-12");
    $[17] = t15;
  } else {
    t15 = $[17];
  }
  let t16;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <AppImage alt="AgroProj" className="h-8 w-32 dark:hidden" height={32} src="/agroproj-logo-text.svg" style={{
      width: "128px",
      height: "32px"
    }} width={128} />;
    $[18] = t16;
  } else {
    t16 = $[18];
  }
  let t17;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <div><Link aria-label="Homepage" href="/">{t16}<AppImage alt="AgroProj" className="hidden h-8 w-32 dark:block" height={32} src="/agroproj-logo-text-dark.svg" style={{
          width: "128px",
          height: "32px"
        }} width={128} /></Link><p className="text-muted-foreground mt-4 max-w-xs text-sm text-pretty">Gestão de projetos agrícolas, do cadastro do produtor ao relatório final.</p></div>;
    $[19] = t17;
  } else {
    t17 = $[19];
  }
  let t18;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <FooterLinkGroup links={[{
      href: "/",
      label: "In\xEDcio"
    }, {
      href: "/planos",
      label: "Planos"
    }, {
      href: "/faq",
      label: "D\xFAvidas"
    }, {
      href: "/suporte",
      label: "Suporte"
    }]} title="Produto" />;
    $[20] = t18;
  } else {
    t18 = $[20];
  }
  let t19;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <FooterLinkGroup links={[{
      href: "/noticias",
      label: "Not\xEDcias"
    }, {
      href: "/legislacao-agro",
      label: "Legisla\xE7\xE3o Agro"
    }]} title={"Conte\xFAdo"} />;
    $[21] = t19;
  } else {
    t19 = $[21];
  }
  let t20;
  let t21;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <div className="flex flex-col gap-10 lg:flex-row lg:justify-between">{t17}<div className="grid grid-cols-2 gap-x-12 gap-y-8 sm:grid-cols-[repeat(3,max-content)] sm:gap-x-16 lg:ml-auto">{t18}{t19}<FooterLinkGroup links={[{
          href: "/privacy",
          label: "Privacidade"
        }, {
          href: "/terms",
          label: "Termos"
        }]} title="Legal" /></div></div>;
    t21 = cn("text-muted-foreground mt-10 border-t pt-8 text-center text-sm sm:text-left", "border-border/50");
    $[22] = t20;
    $[23] = t21;
  } else {
    t20 = $[22];
    t21 = $[23];
  }
  let t22;
  if ($[24] !== currentYear) {
    t22 = <footer className="border-border/50 bg-surface relative z-10 border-t"><div className={t15}>{t20}<p className={t21}>© {currentYear} AgroProj. Todos os direitos reservados.</p></div></footer>;
    $[24] = currentYear;
    $[25] = t22;
  } else {
    t22 = $[25];
  }
  let t23;
  if ($[26] !== children || $[27] !== t13 || $[28] !== t22) {
    t23 = <div className="bg-background relative isolate flex min-h-dvh flex-col antialiased">{t6}{t13}{t14}{children}{t22}</div>;
    $[26] = children;
    $[27] = t13;
    $[28] = t22;
    $[29] = t23;
  } else {
    t23 = $[29];
  }
  return t23;
}
function FooterLinkGroup(t0) {
  const $ = _c(13);
  const {
    links,
    title
  } = t0;
  const pathname = usePathname();
  let t1;
  if ($[0] !== title) {
    t1 = <p className="text-foreground mb-3 text-sm font-medium">{title}</p>;
    $[0] = title;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== links || $[3] !== pathname) {
    let t3;
    if ($[5] !== pathname) {
      t3 = link => {
        const isActive = pathname === link.href || link.href !== "/" && pathname.startsWith(`${link.href}/`);
        return <li key={link.href}><Link aria-current={isActive ? "page" : undefined} className={cn("hover:text-foreground focus-visible:ring-ring focus-visible:ring-offset-surface rounded-sm text-sm font-normal underline-offset-4 transition-colors duration-150 ease-out hover:underline focus-visible:underline focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none motion-reduce:transition-none", isActive ? "text-foreground decoration-brand-primary/70 underline decoration-2" : "text-muted-foreground")} href={link.href}>{link.label}</Link></li>;
      };
      $[5] = pathname;
      $[6] = t3;
    } else {
      t3 = $[6];
    }
    t2 = links.map(t3);
    $[2] = links;
    $[3] = pathname;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[7] !== t2) {
    t3 = <ul className="space-y-2">{t2}</ul>;
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== t1 || $[10] !== t3 || $[11] !== title) {
    t4 = <nav aria-label={title}>{t1}{t3}</nav>;
    $[9] = t1;
    $[10] = t3;
    $[11] = title;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  return t4;
}
function PublicNavLink(t0) {
  const $ = _c(8);
  const {
    href,
    label,
    isActive,
    onNavigate
  } = t0;
  const t1 = isActive ? "page" : undefined;
  const t2 = isActive ? "text-foreground decoration-brand-primary/70 underline decoration-2 underline-offset-4" : "text-muted-foreground";
  let t3;
  if ($[0] !== t2) {
    t3 = cn(HEADER_LINK_CLASSES, t2);
    $[0] = t2;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] !== href || $[3] !== label || $[4] !== onNavigate || $[5] !== t1 || $[6] !== t3) {
    t4 = <Link aria-current={t1} className={t3} href={href} onClick={onNavigate}>{label}</Link>;
    $[2] = href;
    $[3] = label;
    $[4] = onNavigate;
    $[5] = t1;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  return t4;
}
function HeaderNavLinks(t0) {
  const $ = _c(8);
  const {
    pathname,
    className,
    onNavigate
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = cn("flex flex-col gap-4 sm:flex-row sm:items-center sm:gap-5", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== onNavigate || $[3] !== pathname) {
    t2 = PUBLIC_NAV_ITEMS.map(item => <PublicNavLink href={item.href} isActive={item.isActive(pathname)} key={item.href} label={item.label} onNavigate={onNavigate} />);
    $[2] = onNavigate;
    $[3] = pathname;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t1 || $[6] !== t2) {
    t3 = <div className={t1}>{t2}</div>;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  return t3;
}
function HeaderSignInLink(t0) {
  const $ = _c(5);
  const {
    className,
    onNavigate
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = cn(HEADER_LINK_CLASSES, "text-muted-foreground", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== onNavigate || $[3] !== t1) {
    t2 = <Link className={t1} href="/sign-in" onClick={onNavigate}>Entrar</Link>;
    $[2] = onNavigate;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
}
function HeaderPrimaryCta(t0) {
  const $ = _c(14);
  const {
    isSignedIn,
    className,
    compact: t1,
    onNavigate
  } = t0;
  const compact = t1 === undefined ? false : t1;
  if (isSignedIn) {
    let t2;
    if ($[0] !== className) {
      t2 = cn("font-medium", className);
      $[0] = className;
      $[1] = t2;
    } else {
      t2 = $[1];
    }
    let t3;
    if ($[2] !== compact) {
      t3 = compact ? <><span className="sr-only">Acessar Dashboard</span><ArrowRight className="size-4" /></> : <>Acessar Dashboard<ArrowRight className="size-4" /></>;
      $[2] = compact;
      $[3] = t3;
    } else {
      t3 = $[3];
    }
    let t4;
    if ($[4] !== onNavigate || $[5] !== t2 || $[6] !== t3) {
      t4 = <ButtonLink className={t2} size="sm" variant="marketing" href="/app" onClick={onNavigate}>{t3}</ButtonLink>;
      $[4] = onNavigate;
      $[5] = t2;
      $[6] = t3;
      $[7] = t4;
    } else {
      t4 = $[7];
    }
    return t4;
  }
  let t2;
  if ($[8] !== className) {
    t2 = cn("font-medium", className);
    $[8] = className;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  const t3 = compact ? authEntryShortLabel : authEntryLabel;
  let t4;
  if ($[10] !== onNavigate || $[11] !== t2 || $[12] !== t3) {
    t4 = <ButtonLink className={t2} size="sm" variant="marketing" href={authEntryHref} onClick={onNavigate}>{t3}</ButtonLink>;
    $[10] = onNavigate;
    $[11] = t2;
    $[12] = t3;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  return t4;
}
function HeaderAccountActions(t0) {
  const $ = _c(15);
  const {
    isSignedIn,
    className,
    onNavigate
  } = t0;
  let t1;
  if ($[0] !== className) {
    t1 = cn("border-border/60 flex flex-col gap-4 sm:flex-row sm:items-center sm:gap-3 sm:border-l sm:pl-4", className);
    $[0] = className;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== isSignedIn || $[3] !== onNavigate) {
    t2 = !isSignedIn ? <HeaderSignInLink onNavigate={onNavigate} /> : null;
    $[2] = isSignedIn;
    $[3] = onNavigate;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const t3 = !isSignedIn && "w-full sm:w-auto";
  let t4;
  if ($[5] !== t3) {
    t4 = cn(t3);
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== isSignedIn || $[8] !== onNavigate || $[9] !== t4) {
    t5 = <HeaderPrimaryCta className={t4} isSignedIn={isSignedIn} onNavigate={onNavigate} />;
    $[7] = isSignedIn;
    $[8] = onNavigate;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== t1 || $[12] !== t2 || $[13] !== t5) {
    t6 = <div aria-label="Conta" className={t1}>{t2}{t5}</div>;
    $[11] = t1;
    $[12] = t2;
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  return t6;
}
function HeaderActions(t0) {
  const $ = _c(21);
  const {
    isSignedIn
  } = t0;
  const pathname = usePathname();
  if (isSignedIn === undefined) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <div aria-hidden="true" className="flex items-center gap-2"><Skeleton className="hidden h-8 w-40 sm:block" /><Skeleton className="hidden h-8 w-14 sm:block" /><Skeleton className="h-8 w-20 sm:hidden" /><Skeleton className="size-8 sm:hidden" /></div>;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  let t1;
  if ($[1] !== pathname) {
    t1 = <HeaderNavLinks pathname={pathname} />;
    $[1] = pathname;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== isSignedIn) {
    t2 = <HeaderAccountActions isSignedIn={isSignedIn} />;
    $[3] = isSignedIn;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <ThemeModeToggle />;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t1 || $[7] !== t2) {
    t4 = <nav aria-label="Principal" className="hidden items-center gap-6 sm:flex">{t1}{t2}{t3}</nav>;
    $[6] = t1;
    $[7] = t2;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== isSignedIn) {
    t5 = <HeaderPrimaryCta compact={true} isSignedIn={isSignedIn} />;
    $[9] = isSignedIn;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <ThemeModeToggle />;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== isSignedIn || $[13] !== pathname) {
    t7 = <MobileNavMenu isSignedIn={isSignedIn} pathname={pathname} />;
    $[12] = isSignedIn;
    $[13] = pathname;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== t5 || $[16] !== t7) {
    t8 = <div className="flex items-center gap-2 sm:hidden">{t5}{t6}{t7}</div>;
    $[15] = t5;
    $[16] = t7;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== t4 || $[19] !== t8) {
    t9 = <>{t4}{t8}</>;
    $[18] = t4;
    $[19] = t8;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  return t9;
}
function MobileNavMenu(t0) {
  const $ = _c(15);
  const {
    isSignedIn,
    pathname
  } = t0;
  const [open, setOpen] = useState(false);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => setOpen(false);
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const closeMenu = t1;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <SheetTrigger render={<Button aria-label="Abrir menu" size="icon-sm" variant="outline" />}><Menu className="size-4" /></SheetTrigger>;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  let t4;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <SheetHeader><SheetTitle>Menu</SheetTitle></SheetHeader>;
    t4 = <p className="text-muted-foreground mb-3 text-xs font-medium tracking-wide uppercase">Navegação</p>;
    $[2] = t3;
    $[3] = t4;
  } else {
    t3 = $[2];
    t4 = $[3];
  }
  let t5;
  if ($[4] !== pathname) {
    t5 = <div>{t4}<HeaderNavLinks onNavigate={closeMenu} pathname={pathname} /></div>;
    $[4] = pathname;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <p className="text-muted-foreground text-xs font-medium tracking-wide uppercase">Conta</p>;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] !== isSignedIn) {
    t7 = <div className="mt-auto space-y-4">{t6}<HeaderAccountActions className="border-0 pl-0" isSignedIn={isSignedIn} onNavigate={closeMenu} /></div>;
    $[7] = isSignedIn;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] !== t5 || $[10] !== t7) {
    t8 = <SheetContent className="flex w-full flex-col gap-8 sm:max-w-sm" side="right">{t3}{t5}{t7}</SheetContent>;
    $[9] = t5;
    $[10] = t7;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  let t9;
  if ($[12] !== open || $[13] !== t8) {
    t9 = <Sheet onOpenChange={setOpen} open={open}>{t2}{t8}</Sheet>;
    $[12] = open;
    $[13] = t8;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  return t9;
}